import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  IconButton,
  InputAdornment,
  TablePagination,
  Typography,
} from '@mui/material';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { ArrowSquareDownIcon, SearchIcon } from 'assets/icons';
import { PrimaryLightBgCard, PrimaryTextField } from 'components';
import { dateTimeFormat } from 'utils/formats';
import { mediaModalActions } from 'base/common/media-modal/slice';
import { selectMediaResult } from 'base/common/media-modal/selector';
import { IMediaItemData } from 'base/common/media-modal/interface';
import { convertToHLSUrl } from 'components/organisms/streaming-player/utils';
import { AccordionStyle, ThumbnailImgStyle } from '../../styles';

type ExpertMediaFormatted = IMediaItemData & { isStreamAvailable: boolean };

const ExpertMediaItem = ({
  media,
  onSelect,
}: {
  media: ExpertMediaFormatted;
  onSelect: (media: IMediaItemData) => void;
}) => {
  const [isProcessing, setProcessing] = React.useState<boolean>(
    media.isStreamAvailable,
  );

  React.useEffect(() => {
    const checkProcessing = async () => {
      try {
        const convertedSrc = await convertToHLSUrl(media.contentUrl);
        const isProcessed = convertedSrc.endsWith('.m3u8');
        setProcessing(!isProcessed);
        return isProcessed; // Return true if processing is complete
      } catch (error) {
        // In case of error, consider available the raw file
        setProcessing(false);
        return false;
      }
    };

    let interval: NodeJS.Timeout;

    if (media.fileCategory === 'video' && media.contentUrl) {
      // Initial check
      checkProcessing().then((isProcessed) => {
        if (isProcessed) return; // Stop if already processed

        // Poll every 5 seconds if still processing
        interval = setInterval(async () => {
          const isAlreadyProcessed = await checkProcessing();
          if (isAlreadyProcessed) {
            clearInterval(interval); // Stop polling when m3u8 is detected
          }
        }, 5000);
      });
    } else {
      setProcessing(false);
    }

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.isStreamAvailable]);

  return (
    <IconButton
      key={media.id}
      disableRipple
      disabled={isProcessing}
      sx={{ p: 0, display: 'flex', width: '100%' }}
      onClick={() => { onSelect(media); }}
    >
      <PrimaryLightBgCard
        className="flex-grow-1 d-flex flex-nowrap overflow-hidden"
        sx={{
          p: 1.5,
          mb: 1,
          opacity: !isProcessing ? 1 : 0.5,
          filter: !isProcessing ? 'none' : 'grayscale(100%)',
        }}
      >
        <Avatar
          variant="rounded"
          alt="The image"
          src={media.thumbnail}
          sx={ThumbnailImgStyle}
        >
          <PermMediaIcon sx={{ fontSize: 16 }} />
        </Avatar>
        <div className="text-start">
          <Typography variant="body2" sx={{ lineHeight: 1.2, mb: 0.5 }}>
            {media.title}
          </Typography>
          {media.createdAt && media.createdAt.length ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1 }}>
              {dateTimeFormat(media.createdAt)}
            </Typography>
          ) : null}
          {!isProcessing ? null : (
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1, mt: 0.5 }}>
              Processing...
            </Typography>
          )}
        </div>
      </PrimaryLightBgCard>
    </IconButton>
  );
};

const ExpertMedia = ({ onMediaSelect }: { onMediaSelect: (media: IMediaItemData) => void; }) => {
  const dispatch = useDispatch();
  const [keyword, setKeyword] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const isInitiallyLoaded = React.useRef<boolean>(false);
  const [expertMediaList, setExpertMediaList] = React.useState<(ExpertMediaFormatted)[]>([]);
  const { result, total } = useSelector(selectMediaResult);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  React.useEffect(() => () => {
    dispatch(mediaModalActions.resetFetchMedia());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    dispatch(mediaModalActions.fetchMedia({
      keyword,
      page: (page + 1),
      limit: rowsPerPage,
      type: 'image,video',
    }));
  }, [dispatch, page, keyword, rowsPerPage]);

  const handleMediaSelect = useCallback((media: IMediaItemData) => {
    onMediaSelect(media);
  }, [onMediaSelect]);

  React.useEffect(() => {
    if (result.length > 0) {
      const checkAvailability = async () => {
        const expMedia = result.filter(
          (media: IMediaItemData) => !['document', 'audio', 'folder'].includes(media.fileCategory)
            && !media.isExternalFile,
        );
        const updatedList = await Promise.all(expMedia.map(async (media) => {
          // If not a video, it's always available
          if (media.fileCategory !== 'video') {
            return { ...media, isStreamAvailable: true };
          }

          // If not found the content url, it's always not available
          if (!media.contentUrl) {
            return { ...media, isStreamAvailable: false };
          }

          try {
            const convertedSrc = await convertToHLSUrl(media.contentUrl);
            return { ...media, isStreamAvailable: convertedSrc.endsWith('.m3u8') };
          } catch (error) {
            // In case of error, consider available the raw file
            return { ...media, isStreamAvailable: true };
          }
        }));

        setExpertMediaList(updatedList);
      };

      checkAvailability();
    } else {
      setExpertMediaList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  React.useEffect(() => {
    const streamingAvailableMedia = expertMediaList.filter((media) => media.isStreamAvailable);
    if (!isInitiallyLoaded.current && streamingAvailableMedia.length > 0 && page === 0) {
      handleMediaSelect(streamingAvailableMedia[0]);
      isInitiallyLoaded.current = true;
    }
  }, [expertMediaList, handleMediaSelect, page]);

  return (
    <Accordion sx={[AccordionStyle, { mt: 2.5 }]} defaultExpanded disableGutters>
      <AccordionSummary
        expandIcon={<ArrowSquareDownIcon sx={{ color: 'text.secondary', fontSize: 16 }} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography variant="body2" sx={{}}>
          Your media
        </Typography>
      </AccordionSummary>

      <AccordionDetails>

        <PrimaryTextField
          placeholder="Search media"
          sx={{ maxHeight: 38, mb: 2 }}
          endAdornment={(
            <InputAdornment position="end">
              <SearchIcon sx={{ fontSize: 16 }} />
            </InputAdornment>
          )}
          onChange={handleChange}
        />
        {expertMediaList && expertMediaList.length > 0 ? expertMediaList.map((media) => (
          <ExpertMediaItem key={media.id} media={media} onSelect={handleMediaSelect} />
        )) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {keyword && keyword.length > 0 ? 'Media not found from this keyword' : 'You have no media uploaded yet'}
          </Typography>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default ExpertMedia;
